import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export const useNavBadges = (user) => {
  const [projectsCount, setProjectsCount] = useState(0);
  const [permitsCount, setPermitsCount] = useState(0);

  const fetchCounts = useCallback(async () => {
    if (!user) {
      setProjectsCount(0);
      setPermitsCount(0);
      return;
    }

    try {
      // Get organization_id for the current user
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('organization_id')
        ?.eq('id', user?.id)
        ?.single();

      if (!profile?.organization_id) return;

      const orgId = profile?.organization_id;

      // Count active (non-completed) projects
      const { count: activeProjects } = await supabase
        ?.from('projects')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('organization_id', orgId)
        ?.neq('phase', 'completed');

      // Count pending/in-review permits (submission or under review)
      const { count: pendingPermits } = await supabase
        ?.from('permits')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('organization_id', orgId)
        ?.in('status', ['submission', 'under review']);

      setProjectsCount(activeProjects || 0);
      setPermitsCount(pendingPermits || 0);
    } catch (error) {
      console.error('Error fetching nav badge counts:', error);
    }
  }, [user]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    if (!user) return;

    // Real-time subscription for projects table
    const projectsSub = supabase
      ?.channel('nav-badge-projects')
      ?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'projects' },
        () => fetchCounts()
      )
      ?.subscribe();

    // Real-time subscription for permits table
    const permitsSub = supabase
      ?.channel('nav-badge-permits')
      ?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'permits' },
        () => fetchCounts()
      )
      ?.subscribe();

    return () => {
      supabase?.removeChannel(projectsSub);
      supabase?.removeChannel(permitsSub);
    };
  }, [user, fetchCounts]);

  return { projectsCount, permitsCount };
};
